'use client';

import React, { createContext, use, useState, useMemo, useCallback } from 'react';
import { Filters, DEFAULT_FILTERS } from '@/types/filters';
import { track } from '@/utils/analytics';

interface FiltersContextValue {
  filters: Filters;
  updateFilter: <K extends keyof Filters>(key: K, value: Filters[K]) => void;
  resetFilters: () => void;
  activeFilterCount: number;
}

const FiltersContext = createContext<FiltersContextValue | null>(null);

export const FiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);

  const updateFilter = useCallback(<K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    const discrete: (keyof Filters)[] = ['targetAudience', 'onSale', 'countries', 'clusters', 'categories', 'subcategories'];
    if (discrete.includes(key)) {
      const filterValue = value instanceof Set
        ? [...value].join(',')
        : String(value ?? '');
      track({ type: 'filter_applied', filterType: key, filterValue });
    }
  }, []);

  const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  const activeFilterCount = [
    filters.searchQuery !== '',
    filters.mrrRange[0] !== 0 || filters.mrrRange[1] !== Infinity,
    filters.growthRange[0] !== -Infinity || filters.growthRange[1] !== Infinity,
    filters.customersRange[0] !== 0 || filters.customersRange[1] !== Infinity,
    filters.categories.size > 0,
    filters.subcategories.size > 0,
    filters.clusters.size > 0,
    filters.countries.size > 0,
    filters.targetAudience !== null,
    filters.onSale !== null,
  ].filter(Boolean).length;

  const value = useMemo(
    () => ({ filters, updateFilter, resetFilters, activeFilterCount }),
    [filters, updateFilter, resetFilters, activeFilterCount]
  );

  return (
    <FiltersContext.Provider value={value}>
      {children}
    </FiltersContext.Provider>
  );
};

export const useFilters = (): FiltersContextValue => {
  const ctx = use(FiltersContext);
  if (!ctx) throw new Error('useFilters must be used inside FiltersProvider');
  return ctx;
};
